import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { client, sessionUtils } from '../api/client';
import { useI18n } from '../i18n';
import { useAppStore } from '../store/appStore';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { language } = useI18n();
  const navigate = useNavigate();
  const { setUser } = useAppStore();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [message, setMessage] = useState('');
  const [tokensAwarded, setTokensAwarded] = useState(0);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      setMessage(language === 'sr' ? 'Token za verifikaciju nije pronađen.' : 'Verification token not found.');
      return;
    }

    verifyEmail(token);
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      const res = await client.api.fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verificationToken }),
      });

      const data = await res.json();

      if (data.success || data.already_verified) {
        setStatus('success');
        setTokensAwarded(data.tokens || 0);
        setMessage(data.message || (language === 'sr' 
          ? 'Vaš email je uspešno verifikovan!' 
          : 'Your email has been verified successfully!'));
        
        // Auto-login: if the backend returned user + token, save the session
        if (data.user && data.token) {
          sessionUtils.saveSession(data.token, data.user, data.expiresAt || Date.now() + 86400000);
          setUser({
            id: data.user.id,
            email: data.user.email,
            name: data.user.name,
            emailVerified: true,
            type: data.user.userType || data.user.type,
            tokenBalance: data.user.tokenBalance || 0,
            role: data.user.role
          });
          
          // Redirect to profile after a short delay
          setTimeout(() => navigate('/profil'), 3000);
        }
      } else {
        setStatus('error');
        setMessage(data.error || (language === 'sr' 
          ? 'Verifikacija nije uspela. Token je možda istekao ili je već iskorišćen.' 
          : 'Verification failed. The token may have expired or already been used.'));
      }
    } catch (err) {
      setStatus('error');
      setMessage(language === 'sr' 
        ? 'Greška pri komunikaciji sa serverom. Pokušajte ponovo.' 
        : 'Error communicating with server. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-16 flex items-center justify-center">
      <div className="max-w-md mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 text-center"
        >
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Verifikacija u toku...' : 'Verifying...'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'sr' ? 'Molimo sačekajte dok verifikujemo vaš email.' : 'Please wait while we verify your email.'}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Email verifikovan!' : 'Email Verified!'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-4">{message}</p>
              {tokensAwarded > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 dark:text-amber-200 font-semibold">
                    🎉 {language === 'sr' 
                      ? `Dobili ste ${tokensAwarded} besplatnih tokena za dobrodošlicu!` 
                      : `You received ${tokensAwarded} free welcome tokens!`}
                  </p>
                </div>
              )}
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {language === 'sr' 
                  ? 'Automatski ste prijavljeni. Preusmeravanje na profil...' 
                  : 'You are now logged in. Redirecting to profile...'}
              </p>
              <Link 
                to="/profil" 
                className="inline-block px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Idi na profil' : 'Go to Profile'}
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Verifikacija neuspela' : 'Verification Failed'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
              <div className="space-y-3">
                <Link 
                  to="/profil" 
                  className="block px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
                >
                  {language === 'sr' ? 'Zatražite novi link' : 'Request New Link'}
                </Link>
                <Link 
                  to="/" 
                  className="block text-slate-600 dark:text-slate-400 hover:text-amber-500 transition-colors"
                >
                  {language === 'sr' ? 'Nazad na početnu' : 'Back to Home'}
                </Link>
              </div>
            </>
          )}

          {status === 'no-token' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Neispravan link' : 'Invalid Link'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
              <Link 
                to="/" 
                className="inline-block px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Nazad na početnu' : 'Back to Home'}
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
